import React, { useEffect, useRef } from "react";
import ButtonBlack from "@shared/uiNew/Button/button_black";
import * as styles from "./Demo.module.scss";
import { REGISTER_URL } from "@shared/arrays/pop-ups-shared";

export interface DemoProps {
  src: string;
  poster: string;
  label: string;
  cta?: string;
}

/**
 * The editor walkthrough was a 1.3 MB GIF on the Tilda CDN, repeated across
 * several pages. It is an mp4 here, played only while it is on screen and not
 * at all for a visitor who asked for reduced motion, who gets the poster and
 * the controls instead.
 */
export function Demo({ src, poster, label, cta }: DemoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      video.autoplay = false;
      video.controls = true;
      video.pause();
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          void video.play().catch(() => {
            video.controls = true;
          });
        } else {
          video.pause();
        }
      },
      { threshold: 0.2 },
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.wrap}>
      <video
        ref={videoRef}
        className={styles.video}
        src={src}
        poster={poster}
        aria-label={label}
        width={1140}
        height={645}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
      />
      {cta && (
        <ButtonBlack icon="arrow" text={cta} href={REGISTER_URL} type="link" />
      )}
    </div>
  );
}

export default Demo;
